import { ISubscriptionInDTO } from '../../repository/jago/model/input/ISubscriptionInDTO';
import { SubscriptionLogicEnum } from '../../repository/jago/model/input/ISubscriptionLogicEnum';
import { SubscriptionProvisioningTypeEnum } from '../../repository/jago/model/input/ISubscriptionProvisioningTypeEnum';
import { IEditSubscriptionOutDTO, ISubscriptionOutDTO } from '../../repository/jago/model/output/ISubscriptionOutDTO';

/** One parsed (and validated) row of the price list. Money values are in cents (credits * 100). */
export interface IPricelistRow {
  rowNumber: number;
  company: string;
  sku: string;
  name: string;
  resource: string;
  payment: string;
  logic: SubscriptionLogicEnum;
  maxElements: number;
  maxUsers: number;
  maxLocks: number;
  visible: boolean;
  /** Valid main tier subscription types (BASIC, ENTERPRISE, ...) listed in the file, uppercase and deduplicated. */
  mainTierTypes: string[];
  category?: string;
  fixedPrice: number;
  burnRate: number;
  requestToQuote: boolean;
  provisioningType?: SubscriptionProvisioningTypeEnum;
  canBeDeactivated: boolean;
  warnings: string[];
}

export interface IPricelistParseError {
  rowNumber: number;
  company: string;
  sku: string;
  messages: string[];
}

export interface IPricelistParseResult {
  rows: IPricelistRow[];
  errors: IPricelistParseError[];
}

export type PricelistDiffStatus = 'NEW' | 'CHANGED' | 'READONLY_CHANGED' | 'UNCHANGED' | 'ERROR' | 'SKIPPED';

export const PRICELIST_DIFF_STATUSES: PricelistDiffStatus[] = ['NEW', 'CHANGED', 'READONLY_CHANGED', 'UNCHANGED', 'ERROR', 'SKIPPED'];

export type PricelistFieldName =
  | 'fixedPrice'
  | 'burnRate'
  | 'visible'
  | 'provisioningType'
  | 'requestToQuote'
  | 'canBeDeactivated'
  | 'name'
  | 'logic'
  | 'maxElements'
  | 'maxUsers'
  | 'maxLocks'
  | 'resource'
  | 'category';

export interface IFieldChange {
  field: PricelistFieldName;
  oldValue: string | number | boolean | null | undefined;
  newValue: string | number | boolean | null | undefined;
}

/** A single planned subscription (a row expanded for one main tier). */
export interface IPricelistDiffEntry {
  key: string;
  row: IPricelistRow;
  status: PricelistDiffStatus;
  /** Main tier subscription type this entry was expanded for, when any. */
  mainTierType?: string;
  /** Resolved main tier id (existing tier), or null when none / not yet created. */
  mainTierId: number | null;
  /** SKU of a main tier that is created by this same import and therefore resolved at apply time. */
  pendingMainTierSku?: string;
  /** Human label of the main tier (SKU / name) for display. */
  mainTierLabel?: string;
  existing?: ISubscriptionInDTO;
  /** Differences on editable fields (applied through PUT). */
  changes: IFieldChange[];
  /** Differences on non-editable fields (shown as warnings, not applied). */
  readonlyChanges: IFieldChange[];
  errors: string[];
  warnings: string[];
}

export interface IPricelistPlan {
  entries: IPricelistDiffEntry[];
  /** Backend subscriptions that are not described in the file (informational only). */
  onlyInBackend: ISubscriptionInDTO[];
  counts: Record<PricelistDiffStatus, number>;
}

export type PricelistOutcome = 'CREATED' | 'UPDATED' | 'FAILED';

export interface IPricelistApplyResult {
  entry: IPricelistDiffEntry;
  outcome: PricelistOutcome;
  message?: string;
  subscriptionId?: number;
}

export interface IPricelistApplyApi {
  createSubscription(marketplaceId: number, data: ISubscriptionOutDTO): Promise<ISubscriptionInDTO>;
  editSubscription(marketplaceId: number, subscriptionId: number, data: IEditSubscriptionOutDTO): Promise<ISubscriptionInDTO>;
}
